import htmx from 'htmx.org';
import { Params } from '../Shared/scriptts/search-params';
import { getUserObject, setUserObject, saveJobSessionKey } from '../Shared/scriptts/session/sessionmain';
import { defaults, getJobClockDisplay, getRequestVerificationToken, isEmpty, loadAlertBanner } from '../Shared/scriptts/utilities';
import { logSaveJob, logSearchConversion, logApplyJob, logJobDetails } from '../Shared/scriptts/analytics';
import { Job, SaveJobResponse, saveJobStatuses } from '../Shared/scriptts/interfaces';

declare var job: Job;
declare var gMaximumSavedJobs: number;
declare var gMaximumSavedJobsReachedMessage: string;

document.addEventListener('DOMContentLoaded', () => {
    displayStartEndDates();
    initSearchResultsNav();
    displayApplyButton();
    initJoaSaveButton();
    initHeaderObserver();
    initLocationSearch();
    bindEvents();
    logJobDetails(job, getUserObject());
    initMatchedFilterSummary();
});

function initSearchResultsNav() {
    const userObject = getUserObject();
    const backToResultsElem = document.querySelector<HTMLAnchorElement>('#back-to-results');
    const navContainer = document.querySelector<HTMLElement>('#joa-prev-next');
    const prevElem = document.querySelector<HTMLAnchorElement>('#previous-joa');
    const nextElem = document.querySelector<HTMLAnchorElement>('#next-joa');

    if (!backToResultsElem || !navContainer || !prevElem || !nextElem || !userObject) {
        return;
    }

    if (!userObject.SearchResults?.Results?.ReturnUrl || (!document.referrer || document.referrer.toLowerCase().indexOf("/search") === -1 && document.referrer.toLowerCase().indexOf("/job") === -1)) {
        backToResultsElem.classList.add('hidden');
        navContainer.classList.add('hidden');
        return;
    }

    let jobsList = userObject.SearchResults?.Results?.Items || [];
    const currentJob = location.pathname.split('/').pop() || '';
    const currentJobPosition = currentJob === '' ? -1 : jobsList.indexOf(currentJob);
    let previousJob = jobsList[currentJobPosition - 1] || null;
    let nextJob = jobsList[currentJobPosition + 1] || null;
    const page = parseInt(userObject.SearchResults.Params.Page || '1')
    let returnUrl = new URL(userObject.SearchResults.Results.ReturnUrl);

    if (userObject.SearchResults.Params.Page) {
        returnUrl.searchParams.set('p', userObject.SearchResults.Params.Page);
    }

    backToResultsElem.href = returnUrl.toString();

    if (previousJob === null) {
        if (page < 2) {
            prevElem.classList.add('hidden');
        }
        else {
            getJobsFromIndex(userObject.SearchResults.Params, (page - 1).toString())
                .then(data => {
                    const dataJobsList = <string[]>JSON.parse(data);

                    jobsList = dataJobsList.concat(jobsList);
                    userObject.SearchResults.Results.Items = jobsList;
                    if (userObject.SearchResults.Results.PageSets.findIndex(p => p.Page === page - 1) === -1) {
                        userObject.SearchResults.Results.PageSets.push({ Page: page - 1, PageItems: dataJobsList });
                    }

                    setJobNavAttributes(prevElem, jobsList[jobsList.indexOf(currentJob) - 1]);
                    setUserObject(userObject);
                })
                .catch(err => console.error(err));
        }
    }
    else {
        setJobNavAttributes(prevElem, previousJob);
    }

    if (nextJob === null) {
        const totalPages = Math.ceil(userObject.SearchResults.Results.ResultCount / (userObject.SearchResults.Params.ResultsPerPage || defaults.resultsPerPage));

        if (page >= totalPages) {
            nextElem.classList.add('hidden');
        }
        else {
            getJobsFromIndex(userObject.SearchResults.Params, (page + 1).toString())
                .then(data => {
                    const dataJobsList = <string[]>JSON.parse(data);

                    jobsList = jobsList.concat(dataJobsList);
                    userObject.SearchResults.Results.Items = jobsList;
                    if (userObject.SearchResults.Results.PageSets.findIndex(p => p.Page === page + 1) === -1) {
                        userObject.SearchResults.Results.PageSets.push({ Page: page + 1, PageItems: dataJobsList });
                    }

                    setJobNavAttributes(nextElem, jobsList[jobsList.indexOf(currentJob) + 1]);
                    setUserObject(userObject);
                })
                .catch(err => console.error(err));
        }
    }
    else {
        setJobNavAttributes(nextElem, nextJob);
    }
}

function setJobNavAttributes(elem: HTMLAnchorElement, docid: string) {
    elem.href = `/job/${docid}`;
    elem.setAttribute('data-docid', docid);
    elem.addEventListener('click', resultsNavClicked);
}

async function getJobsFromIndex(params: Params, page: string) {
    const requestVerificationToken = getRequestVerificationToken();
    const tempParams = structuredClone(params);
    tempParams.Page = page;
    return fetch('/search/executenextsearch',
        {
            method: 'post',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                ...(requestVerificationToken ? { "RequestVerificationToken": requestVerificationToken } : {})
            },
            body: JSON.stringify(tempParams),
            credentials: 'same-origin'
        })
        .then(response => response.text());
}

function initJoaSaveButton() {
    displaySaveButton();
    document.querySelector<HTMLButtonElement>('#joa-save-button')?.addEventListener('click', saveJobClicked);
}

function displaySaveButton() {
    const userObject = getUserObject();
    const buttonElem = document.querySelector<HTMLButtonElement>('#joa-save-button');
    const iconElem = document.querySelector<HTMLElement>('#joa-save-icon');
    const textElem = document.querySelector<HTMLElement>('#joa-save-text');

    if (!userObject.IsAuthenticated || !buttonElem || !iconElem || !textElem) {
        return;
    }
    const docId = buttonElem.getAttribute('data-document-id') || '';
    const savedIconHref = '/img/icons/sprite.svg#star';
    const unsavedIconHref = '/img/icons/sprite.svg#star_outline';

    if (docId in userObject.SavedJobs) {
        buttonElem.setAttribute('data-state', 'is-saved');
        iconElem.querySelector('use')?.setAttribute('href', savedIconHref);
        textElem.innerText = 'Saved to My jobs';
    }
    else {
        buttonElem.setAttribute('data-state', 'is-unsaved');
        iconElem.querySelector('use')?.setAttribute('href', unsavedIconHref);
        textElem.innerText = 'Save';
    }
}

function applyClicked(evt: Event) {
    const applyElem = document.querySelector<HTMLAnchorElement>('#joa-apply-button');
    if (!applyElem) {
        return;
    }
    evt.preventDefault();

    const userObject = getUserObject();

    if (!userObject.IsAuthenticated) {
        localStorage.setItem("logApplyJobAfterSignIn", JSON.stringify(job));
    }
    else if (!userObject.AppliedJobs?.hasOwnProperty(job.DocumentID)) {
        logApplyJob(job);
    }
    logSearchConversion(getUserObject(), "search_conversion_apply");
    location.href = applyElem.href;
}

function displayApplyButton() {
    const applyElem = document.querySelector<HTMLElement>('#joa-apply-button');
    if (!applyElem) {
        return;
    }

    const userObject = getUserObject();
    applyElem.addEventListener('click', applyClicked, { once: true });

    if (userObject.IsAuthenticated) {
        const documentId = applyElem.getAttribute('data-document-id') || '';

        fetch(`/job/${documentId}/applytext`, {
            method: 'GET',
            mode: 'cors',
            cache: 'no-cache',
            headers: { "Content-Type": "application/json" }
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Response returned ${response.status}`);
                }
                return response.json();
            })
            .then(result => applyElem.innerText = result)
            .catch(reason => console.debug(reason));
    }
}

function saveJobClicked(evt: Event) {
    const elem = document.querySelector<HTMLButtonElement>('#joa-save-button');
    if (!elem) {
        return;
    }

    const documentId = getDocumentId(elem);
    if (documentId === null) {
        console.debug('invalid document id');
        return;
    }

    const state = elem.getAttribute('data-state');
    const userObject = getUserObject();

    if (state === 'is-saved') {
        saveJob(documentId, false);
    }
    else if (state === 'is-unsaved') {
        if (!userObject.IsAuthenticated) {
            localStorage.setItem(saveJobSessionKey, JSON.stringify({ 'docId': documentId, 'save': 'true', 'joa': job }));
            location.href = `/Applicant/Profile/QuickSave/${documentId}`;
            return;
        }

        if (hasReachedSavedJobsLimit(userObject)) {
            evt.preventDefault();
            showSaveJobLimitAlert();
            return;
        }

        saveJob(documentId, true);
    }
    else {
        console.debug('invalid save state');
    }
}

function saveJob(documentId: string, save: boolean) {
    const userObject = getUserObject();
    if (save && userObject.IsAuthenticated && hasReachedSavedJobsLimit(userObject)) {
        showSaveJobLimitAlert();
        return;
    }

    const requestVerificationToken = getRequestVerificationToken();
    fetch(`/search/savejob/${documentId}/${save}`, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            "Content-Type": "application/json",
            ...(requestVerificationToken ? { "RequestVerificationToken": requestVerificationToken } : {})
        },
        credentials: 'same-origin'
    })
        .then(response => {
            if (!response.ok) {
                throw new Error(`Response returned ${response.status}`);
            }
            return response.json() as Promise<SaveJobResponse>;
        })
        .then(result => handleJobSaveResult(documentId, save, result))
        .then(displaySaveButton)
        .catch(reason => console.debug(reason));
}

function handleJobSaveResult(documentId: string, save: boolean, result: SaveJobResponse) {
    const userObject = getUserObject();

    if (result.Status === saveJobStatuses.maximumReached) {
        showSaveJobLimitAlert(result.Message);
        return;
    }

    if (result.Status === saveJobStatuses.saved) {
        userObject.SavedJobs[documentId] = true;
    }
    else if (result.Status === saveJobStatuses.deleted) {
        delete userObject.SavedJobs[documentId];
        clearPositionMessages();
    }
    else {
        throw new Error(`Invalid save job status: ${result.Status}`);
    }

    setUserObject(userObject);
    logSaveJob(job, save);
}

function hasReachedSavedJobsLimit(userObject: ReturnType<typeof getUserObject>): boolean {
    const savedJobs = userObject.SavedJobs || {};
    return Object.keys(savedJobs).length >= gMaximumSavedJobs;
}

function showSaveJobLimitAlert(message: string = gMaximumSavedJobsReachedMessage) {
    const positionMessages = document.querySelector<HTMLElement>('#position-messages');
    if (!positionMessages) {
        return;
    }

    loadAlertBanner(positionMessages, { alertType: 'Error', message, isDismissable: true });
}

function clearPositionMessages() {
    const positionMessages = document.querySelector<HTMLElement>('#position-messages');
    if (!positionMessages) {
        return;
    }

    positionMessages.replaceChildren();
}

function getDocumentId(elem: HTMLButtonElement): string | null {
    const documentId = elem.getAttribute('data-document-id');
    if (!documentId) {
        return null;
    }

    const isNumericDocumentId = /^\d+$/.test(documentId);
    const isPositiveDocumentId = /[1-9]/.test(documentId);
    if (!isNumericDocumentId || !isPositiveDocumentId) {
        return null;
    }

    return documentId;
}

function initHeaderObserver() {
    const joaHeader = document.querySelector<HTMLElement>('#joa-header');
    const observerTarget = document.querySelector<HTMLElement>('#back-to-results-container');
    const applyMobile = document.querySelector<HTMLAnchorElement>('#joa-apply-mobile');
    const mobileSpacer = document.querySelector<HTMLElement>('#mobile-spacer');

    if (!joaHeader || !observerTarget || !applyMobile || !mobileSpacer) {
        return;
    }

    const observer = new IntersectionObserver((ioEntries) => {
        ioEntries.forEach(e => {
            if (e.isIntersecting) {
                joaHeader.classList.remove('hidden');
                applyMobile.classList.add('hidden');
                mobileSpacer.classList.add('hidden');
            }
            else {
                joaHeader.classList.add('hidden');
                applyMobile.classList.remove('hidden');
                mobileSpacer.classList.remove('hidden');
            }
        })
    }, {
        root: null,
        threshold: 1
    });
    observer.observe(observerTarget);
}

function resultsNavClicked(evt: Event) {
    const elem = <HTMLAnchorElement>(evt.currentTarget);
    const docid = elem.getAttribute('data-docid') || '';
    const userObject = getUserObject();

    if (!userObject || !userObject.SearchResults.Results.PageSets) {
        return;
    }

    for (let i = 0; i < userObject.SearchResults.Results.PageSets.length; i++) {
        if (userObject.SearchResults.Results.PageSets[i].PageItems.indexOf(docid) >= 0) {
            userObject.SearchResults.Params.Page = userObject.SearchResults.Results.PageSets[i].Page.toString();
            setUserObject(userObject);
            return;
        }
    }
}

function bindEvents() {
    addEventListener('pageshow', hashChanged);
    addEventListener('hashchange', hashChanged);
    htmx.on('htmx:afterSwap', () => {
        initJoaSaveButton();
    });
}

function hashChanged() {
    const defaultCss = 'button button-unstyled no-underline py-4 px-6 text-sm';
    const activeCss = ' font-bold text-black border-b-4 border-black';
    const hash = window.location.hash;

    if (!hash) {
        return;
    }

    document.querySelectorAll<HTMLAnchorElement>('[data-joa-nav]').forEach(elem => {
        elem.className = defaultCss;

        if (elem.href.substring(elem.href.lastIndexOf('#')) === hash.trim()) {
            elem.className += activeCss;
        }
    });

    const target = document.querySelector<HTMLElement>(hash);
    if (!target) {
        return;
    }

    const offset = 225;
    scrollTo({
        top: target.offsetTop - offset,
        behavior: 'instant'
    });
}

function filterLocations(input: HTMLInputElement) {
    const container = input.closest<HTMLElement>('[data-location-search-container]');
    const list = container?.querySelectorAll<HTMLElement>('[data-location-text]');
    const emptyElem = container?.querySelector<HTMLElement>('[data-location-empty]');

    if (!container || !list || !emptyElem) {
        return;
    }

    const term = input.value.trim().toLowerCase();
    const termIsEmpty = isEmpty(term);
    let hasVisibleItems = false;

    for (const locationElem of list) {
        const searchableText = (locationElem.dataset.locationText || locationElem.textContent || '').trim().toLowerCase();
        const isVisible = termIsEmpty || searchableText.includes(term);

        locationElem.classList.toggle('hidden', !isVisible);

        if (isVisible) {
            hasVisibleItems = true;
        }
    }

    emptyElem?.classList.toggle('hidden', hasVisibleItems);
}

function initLocationSearch() {
    document.querySelectorAll<HTMLInputElement>('[data-location-search]').forEach(input => {
        if (input.dataset.locationSearchBound === 'true') {
            return;
        }

        input.addEventListener('input', () => filterLocations(input));
        input.addEventListener('search', () => filterLocations(input));
        input.dataset.locationSearchBound = 'true';
    });
}

function initMatchedFilterSummary() {
    const userObject = getUserObject();
    const defaultFilters = userObject.DefaultFilters;
    let filterCount = 0;
    if (defaultFilters) {
        const hiddenInput = document.getElementById('joaFilterQuery') as HTMLInputElement;
        if (hiddenInput) {
            const postionFilters: Params = JSON.parse(hiddenInput.value);
            if (postionFilters) {
                if (defaultFilters.HiringPath && defaultFilters.HiringPath.length > 0 && postionFilters.HiringPath && postionFilters.HiringPath?.length > 0) {
                    for (const dfHiringPath of defaultFilters.HiringPath) {
                        if (postionFilters.HiringPath.indexOf(dfHiringPath) > -1) {
                            filterCount++;
                            break;
                        }
                    }
                }

                if (defaultFilters.GradeBucket && postionFilters.GradeBucket) {
                    for (const dfGrade of defaultFilters.GradeBucket) {
                        if (postionFilters.GradeBucket.indexOf(dfGrade) > -1) {
                            filterCount++;
                            break;
                        }
                    }
                }

                if (defaultFilters.RemunerationMinimumAmount && postionFilters.RemunerationMinimumAmount) {
                    const defaultMinimum = Number(defaultFilters.RemunerationMinimumAmount);
                    const positionMaximum = Number(postionFilters.RemunerationMaximumAmount);

                    if (!Number.isNaN(defaultMinimum) && !Number.isNaN(positionMaximum) && defaultMinimum > 0 && positionMaximum >= defaultMinimum) {
                        filterCount++;
                    }
                }

                if (defaultFilters.SecurityClearanceRequired && postionFilters.SecurityClearanceRequired) {
                    for (const dfClearance of defaultFilters.SecurityClearanceRequired) {
                        if (postionFilters.SecurityClearanceRequired.indexOf(dfClearance) > -1) {
                            filterCount++;
                            break;
                        }
                    }
                }

                if (defaultFilters.PositionOfferingTypeCode && postionFilters.PositionOfferingTypeCode) {
                    for (const dfCategory of defaultFilters.PositionOfferingTypeCode) {
                        if (postionFilters.PositionOfferingTypeCode.indexOf(dfCategory) > -1) {
                            filterCount++;
                            break;
                        }
                    }
                }

                if (defaultFilters.TravelPercentage && postionFilters.TravelPercentage) {
                    for (const dfTravelPercentage of defaultFilters.TravelPercentage) {
                        if (postionFilters.TravelPercentage.indexOf(dfTravelPercentage) > -1) {
                            filterCount++;
                            break;
                        }
                    }
                }

                if (defaultFilters.PositionScheduleTypeCode && postionFilters.PositionScheduleTypeCode) {
                    for (const dfScheduleCode of defaultFilters.PositionScheduleTypeCode) {
                        if (postionFilters.PositionScheduleTypeCode.indexOf(dfScheduleCode) > -1) {
                            filterCount++;
                            break;
                        }
                    }
                }

                if (defaultFilters.JobCategoryCode && postionFilters.JobCategoryCode) {
                    for (const dfCategory of defaultFilters.JobCategoryCode) {
                        if (postionFilters.JobCategoryCode.indexOf(dfCategory) > -1) {
                            filterCount++;
                            break;
                        }
                    }
                }

                var agencyMatched = false;
                if (!defaultFilters.ExcludeOrganizations) {
                    if (defaultFilters.Agency && postionFilters.Agency) {
                        for (const organization of defaultFilters.Agency) {
                            if (postionFilters.Agency.includes(organization)) {
                                agencyMatched = true;
                                filterCount++;
                                break;
                            }
                        }
                    }

                    if (agencyMatched == false && defaultFilters?.Department && postionFilters.Department) {
                        for (const department of defaultFilters.Department) {
                            if (postionFilters.Department.includes(department)) {
                                filterCount++;
                                break;
                            }
                        }
                    }
                }

                if (defaultFilters.RemoteIndicator && defaultFilters.RemoteIndicator == postionFilters.RemoteIndicator) {
                    filterCount++;
                } else if (defaultFilters.TeleworkIndicator && defaultFilters.TeleworkIndicator == postionFilters.TeleworkIndicator) {
                    filterCount++;
                }
            }

            if (filterCount > 0) {
                displayMatchedFilterSummary(filterCount);
            }
        }
    }
}

function displayMatchedFilterSummary(filterCount) {
    const allDivs = document.querySelectorAll('div[name="matchedDefaultFilters"]');
    const matchedFilterText = filterCount === 1
        ? 'Matches 1 default filter'
        : `Matches ${filterCount} default filters`;

    allDivs.forEach((currentDiv) => {
        currentDiv.className = "p-4 border-1 border-accent-cool bg-info-lighter mb-2";
        currentDiv.innerHTML = `<svg class="fill-success-dark w-5 h-5 mr-1 inline" role="presentation"><use href="/img/icons/sprite.svg#check"></use></svg> ${matchedFilterText}`;
    });
}

function displayStartEndDates() {
    document.querySelectorAll<HTMLElement>('[data-id=open-dates]').forEach(elem => elem.innerHTML = getJobClockDisplay(job));
}
